import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

/**
 * Chapter 5: Hermetic Principles
 * Caduceus with double helix and central staff
 */
export default function Caduceus({ audioData, isActive, position = [0, 0, 0] }) {
  const groupRef = useRef()
  const helix1Ref = useRef()
  const helix2Ref = useRef()
  const staffRef = useRef()
  const energyRefs = useRef([])

  const { fft, bass, mids, highs, amplitude, beat } = audioData || {
    fft: new Float32Array(128).fill(0),
    bass: 0,
    mids: 0,
    highs: 0,
    amplitude: 0,
    beat: 0
  }

  // Create helix paths
  const { helix1Curve, helix2Curve } = useMemo(() => {
    const points1 = []
    const points2 = []
    const height = 4
    const radius = 0.8
    const turns = 3

    for (let i = 0; i <= 100; i++) {
      const t = i / 100
      const y = t * height - height / 2
      const angle1 = t * Math.PI * 2 * turns
      const angle2 = angle1 + Math.PI

      points1.push(
        new THREE.Vector3(
          Math.cos(angle1) * radius,
          y,
          Math.sin(angle1) * radius
        )
      )

      points2.push(
        new THREE.Vector3(
          Math.cos(angle2) * radius,
          y,
          Math.sin(angle2) * radius
        )
      )
    }

    return {
      helix1Curve: new THREE.CatmullRomCurve3(points1),
      helix2Curve: new THREE.CatmullRomCurve3(points2)
    }
  }, [])

  // Create tube geometries
  const helix1Geometry = useMemo(() => {
    return new THREE.TubeGeometry(helix1Curve, 100, 0.08, 8, false)
  }, [helix1Curve])

  const helix2Geometry = useMemo(() => {
    return new THREE.TubeGeometry(helix2Curve, 100, 0.08, 8, false)
  }, [helix2Curve])

  // Energy pulse positions
  const energyPulses = useMemo(() => {
    return Array.from({ length: 6 }, (_, i) => ({
      id: i,
      offset: i / 6,
      speed: 0.2 + Math.random() * 0.1
    }))
  }, [])

  useFrame((state, delta) => {
    if (!isActive) return

    const time = state.clock.elapsedTime

    // Rotate entire caduceus
    if (groupRef.current) {
      groupRef.current.rotation.y += delta * 0.1 + amplitude * 0.02
    }

    // Pulse helixes with audio
    if (helix1Ref.current) {
      const scale = 1 + bass * 0.1
      helix1Ref.current.scale.set(scale, 1, scale)
    }

    if (helix2Ref.current) {
      const scale = 1 + mids * 0.1
      helix2Ref.current.scale.set(scale, 1, scale)
    }

    // Staff glow intensity
    if (staffRef.current && staffRef.current.material) {
      staffRef.current.material.emissiveIntensity = 0.5 + amplitude * 1.5
    }

    // Energy pulses traveling along helixes
    energyRefs.current.forEach((pulse, index) => {
      if (pulse) {
        const energyData = energyPulses[index]
        const t = (time * energyData.speed + energyData.offset) % 1

        // Alternate between helix paths
        const curve = index % 2 === 0 ? helix1Curve : helix2Curve
        const point = curve.getPoint(t)
        pulse.position.copy(point)

        // Scale with beat
        const baseScale = 0.15 + highs * 0.1
        pulse.scale.setScalar(baseScale)

        // Glow intensity
        if (pulse.material) {
          pulse.material.emissiveIntensity = 2 + beat * 3
        }
      }
    })
  })

  return (
    <group ref={groupRef} position={position}>
      {/* Central staff */}
      <mesh ref={staffRef}>
        <cylinderGeometry args={[0.06, 0.06, 4.2, 16]} />
        <meshStandardMaterial
          color="#FFD700"
          metalness={1}
          roughness={0.2}
          emissive="#FFD700"
          emissiveIntensity={0.5}
        />
      </mesh>

      {/* First helix */}
      <mesh ref={helix1Ref} geometry={helix1Geometry}>
        <meshStandardMaterial
          color="#FFD700"
          metalness={0.9}
          roughness={0.1}
          emissive="#FFA500"
          emissiveIntensity={0.3}
        />
      </mesh>

      {/* Second helix */}
      <mesh ref={helix2Ref} geometry={helix2Geometry}>
        <meshStandardMaterial
          color="#FFD700"
          metalness={0.9}
          roughness={0.1}
          emissive="#FFA500"
          emissiveIntensity={0.3}
        />
      </mesh>

      {/* Energy pulses */}
      {energyPulses.map((pulse, index) => (
        <mesh
          key={pulse.id}
          ref={(el) => (energyRefs.current[index] = el)}
        >
          <sphereGeometry args={[0.15, 16, 16]} />
          <meshStandardMaterial
            color="#FFFFFF"
            emissive="#FFFF00"
            emissiveIntensity={2}
            transparent
            opacity={0.9}
          />
        </mesh>
      ))}

      {/* Top sphere (wings metaphor) */}
      <mesh position={[0, 2.3, 0]}>
        <sphereGeometry args={[0.2, 16, 16]} />
        <meshStandardMaterial
          color="#FFD700"
          metalness={1}
          roughness={0.1}
          emissive="#FFD700"
          emissiveIntensity={1}
        />
      </mesh>

      {/* Bottom sphere */}
      <mesh position={[0, -2.3, 0]}>
        <sphereGeometry args={[0.2, 16, 16]} />
        <meshStandardMaterial
          color="#FFD700"
          metalness={1}
          roughness={0.1}
          emissive="#FFD700"
          emissiveIntensity={1}
        />
      </mesh>

      {/* Point lights at top and bottom */}
      <pointLight position={[0, 2.3, 0]} intensity={2} color="#FFD700" distance={4} />
      <pointLight position={[0, -2.3, 0]} intensity={2} color="#FFD700" distance={4} />
    </group>
  )
}
